import {  useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Container,
    Form,
    OverlayTrigger,
    Row,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { getSapUnpaidInvoiceCompanies } from "./hooks/api";
import { useHandleGenerateSoa } from "./hooks/useGenerateSoa";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import { AsyncPaginate } from "react-select-async-paginate";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";

const emptyCompanyRow = () => ({
    id: crypto.randomUUID(),
    bp_name: "",
    bp_code: "",
    remarks: "",
});

const GenerateSoa = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([emptyCompanyRow()]);
    const [searchValue, setSearchValue] = useState({
        start_date: "2024-01-01", // string format
        end_date: dayjs().format("YYYY-MM-DD"), // today as string
        as_at_date: dayjs().format("YYYY-MM-DD"), // today as string
    });
    const { generate, loading, error, setError } = useHandleGenerateSoa();

    const loadOptions = async (search, loadedOptions, { page }) => {
        const response = await getSapUnpaidInvoiceCompanies({
            search,
            page,
        });

        return {
            options: response.data.map((company) => ({
                value: company.bp_code,
                label: company.bp_name,
            })),
            hasMore: response.has_more,
            additional: {
                page: page + 1,
            },
        };
    };

    const handleGenerate = async () => {
        const validRows = rows.filter((row) => row.bp_code && row.bp_name);

        if (validRows.length === 0) {
            setError("Please select at least one company!");
            return;
        }

        if (!searchValue.as_at_date) {
            setError("Please select As At Date");
        }

        if (!searchValue.start_date || !searchValue.end_date) {
            setError("Please select start date and end date");
        }

        await generate({
            as_at_date: searchValue.as_at_date,
            start_date: searchValue.start_date,
            end_date: searchValue.end_date,
            companies: validRows.map(({ bp_code, bp_name, remarks }) => ({
                bp_code,
                bp_name,
                remarks,
            })),
        });
    };

    const handleAddCompany = () => {
        setRows((prev) => [...prev, emptyCompanyRow()]);
    };

    const handleRemoveCompany = (id) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    };

    const handleCompanyChange = (id, option) => {
        setRows((prev) =>
            prev.map((row) =>
                row.id === id
                    ? {
                          ...row,
                          bp_code: option?.value ?? "",
                          bp_name: option?.label ?? "",
                      }
                    : row
            )
        );
    };

    const handleFieldChange = (id, field, value) => {
        setRows((prev) =>
            prev.map((row) =>
                row.id === id
                    ? {
                          ...row,
                          [field]: value,
                      }
                    : row
            )
        );
    };

    return (
        <>
            <Helmet title="Generate Statement of Account" />

            <Container fluid>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0">Generate Statement of Account</h1>

                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Return</Tooltip>}
                    >
                        <Button
                            className="ms-2"
                            onClick={() =>
                                navigate("/tms/sap-unpaid-invoice-uploads")
                            }
                            disabled={loading}
                        >
                            <ArrowLeft className="feather" />

                            {loading && (
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="ms-2"
                                />
                            )}
                        </Button>
                    </OverlayTrigger>
                </div>

                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Companies</h5>

                        <Button onClick={handleGenerate} disabled={loading}>
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Generating...
                                </>
                            ) : (
                                "Download SOA"
                            )}
                        </Button>
                    </Card.Header>

                    <Card.Body>
                        {/* Date Range */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Form.Label>As At Date</Form.Label>
                                <DatePicker
                                    id="from-date-picker"
                                    value={
                                        searchValue.as_at_date
                                            ? moment(
                                                  searchValue.as_at_date,
                                                  "YYYY-MM-DD"
                                              )
                                            : null
                                    }
                                    name="from_date"
                                    className="form-control"
                                    placeholder="As At Date"
                                    onChange={(e) =>
                                        setSearchValue({
                                            ...searchValue,
                                            as_at_date: e
                                                ? moment(e).format("YYYY-MM-DD")
                                                : null,
                                        })
                                    }
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>From Date</Form.Label>
                                <DatePicker
                                    id="from-date-picker"
                                    value={
                                        searchValue.start_date
                                            ? moment(
                                                  searchValue.start_date,
                                                  "YYYY-MM-DD"
                                              )
                                            : null
                                    }
                                    name="from_date"
                                    className="form-control"
                                    placeholder="From date"
                                    onChange={(e) =>
                                        setSearchValue({
                                            ...searchValue,
                                            start_date: e
                                                ? moment(e).format("YYYY-MM-DD")
                                                : null,
                                        })
                                    }
                                />
                            </Col>

                            <Col md={3}>
                                <Form.Label>To Date</Form.Label>
                                <DatePicker
                                    id="to-date-picker"
                                    name="to_date"
                                    className="form-control"
                                    value={
                                        searchValue.end_date
                                            ? moment(
                                                  searchValue.end_date,
                                                  "YYYY-MM-DD"
                                              )
                                            : null
                                    }
                                    placeholder="To date"
                                    onChange={(e) =>
                                        setSearchValue({
                                            ...searchValue,
                                            end_date: e
                                                ? moment(e).format("YYYY-MM-DD")
                                                : null,
                                        })
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col>
                                <small className="text-danger">
                                    * The <strong>From Date</strong> and{" "}
                                    <strong>To Date</strong> apply only to TMS
                                    unpaid invoices.
                                </small>
                            </Col>
                        </Row>
                        <hr className="mb-4" />

                        {/* Company Section Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Company List</h6>

                            <Button
                                variant="outline-primary"
                                onClick={handleAddCompany}
                            >
                                + Add Company
                            </Button>
                        </div>

                        {rows.map((row) => (
                            <Row
                                key={row.id}
                                className="g-3 align-items-end mb-4"
                            >
                                <Col md={5}>
                                    <Form.Label>Company</Form.Label>
                                    <AsyncPaginate
                                        className="render-above"
                                        loadOptions={loadOptions}
                                        additional={{ page: 1 }}
                                        placeholder="Search Company"
                                        isClearable
                                        onChange={(option) =>
                                            handleCompanyChange(row.id, option)
                                        }
                                    />
                                </Col>

                                <Col md={6}>
                                    <Form.Label>Remarks (Optional)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={1}
                                        value={row.remarks}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                row.id,
                                                "remarks",
                                                e.target.value
                                            )
                                        }
                                    />
                                </Col>

                                <Col
                                    md={1}
                                    className="d-flex justify-content-end"
                                >
                                    {rows.length > 1 && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                                handleRemoveCompany(row.id)
                                            }
                                        >
                                            &times;
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        ))}

                        {error && (
                            <>
                                <hr />
                                <Alert className="p-2 mb-0" variant="danger">
                                    {error}
                                </Alert>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default GenerateSoa;
